import { Alert, Anchor, Badge, Group, Modal, Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { DataTable } from 'mantine-datatable';
import { Link } from 'react-router';
import { rawApi } from '../../api/client';
import { errorCopy } from '../../api/errors';
import { listKey } from '../../api/queryKeys';
import { type CashPair, MoneyPair } from '../../components/CashFigures';
import { useEntityIndex } from '../../hooks/useEntityIndex';
import { enumColor, enumLabel } from '../../lib/enums';
import { formatDateTime } from '../../lib/format';

/**
 * Why the safe holds what it holds.
 *
 * The figure on the register screen is DERIVED and never stored — the last count the owner checked,
 * plus everything that has moved since, less everything that has gone to the bank, less every bill
 * paid straight out of the box. That is the right way to hold it and it leaves the owner with a
 * number and no way to argue with it. A safe that reads eight thousand lempiras short of what he
 * expected is either a night that was never dropped, a deposit recorded twice, or money gone, and
 * the three look identical until somebody can see the movements themselves.
 *
 * So this is the working behind that one figure, and it is deliberately the whole of it: every
 * transfer in, every transfer out, every deposit, every bill paid out of the box and every payment
 * taken back into it, oldest last. Nothing here can be edited — a movement is recorded where it
 * happened, at the register, on the deposit form or on the bill it settled, and a ledger you can
 * type into is not a ledger.
 *
 * ## THE COLUMN THAT SAID "WITNESSED BY" AND COULD ONLY EVER SAY "NOBODY"
 *
 * It read `verifiedByName`, which on a transfer names the witness a night drop used to demand. A
 * witness is a second person and there is one bartender on a shift, so the server retired the field
 * and now sends null on every transfer for ever — meaning the column printed **Nobody** against
 * every drop this bar will ever make, beside a note calling that blank "the thing worth noticing".
 * The owner reading it would conclude nobody was checking his takings, and he would be reading a
 * hard-coded null.
 *
 * What is really there is his own check, and the endpoint has been sending it all along:
 * `checkedByName` on a drop he has opened and counted, `verifiedByName` on a deposit he has found
 * on the statement. One question — *has he looked at this one yet* — so one column, and the pouch
 * that came up short says so beside his name instead of somewhere else.
 */

interface LedgerEntry {
  id: string;
  at: string;
  /** `paidOut` and `paidIn` are BOX events: a bill settled straight out of the box, and back. */
  kind: 'transferIn' | 'transferOut' | 'bankDeposit' | 'paidOut' | 'paidIn';
  fromName: string | null;
  toName: string | null;
  amount: CashPair;
  byName: string | null;
  /** On a DEPOSIT, who checked it off against the statement. Null on a transfer, always. */
  verifiedByName: string | null;
  /** On a TRANSFER, who opened the pouch and counted it. Null on a deposit, always. */
  checkedByName: string | null;
  /** What was actually in the pouch, against `amount`, which is what was counted into it. */
  found: CashPair | null;
  /** Set when the movement was a drop out of a drawer, which says which kind of drop it was. */
  safeDropKind: 'nightDrop' | 'midShift' | null;
  reason: string | null;
  /** On a box paid-out, the photographed receipt — "receipts plus cash should still total". */
  receiptPhotoR2Key: string | null;
  /** On a box event, the record that caused it: the supplier payment behind the water invoice. */
  linkedEntityType: string | null;
  linkedEntityId: string | null;
}

/**
 * Who checked this movement, whichever kind it is. Null means nobody has yet.
 *
 * The two fields are one fact wearing two names — the office attesting to a movement it did not
 * make — and only ever one of them is filled, because a pouch is counted and a deposit is found on
 * a statement. Collapsing them here rather than printing two columns that are each blank half the
 * time is the difference between a screen that answers the question and a screen that asks it twice.
 */
const checkedBy = (entry: LedgerEntry): string | null => entry.checkedByName ?? entry.verifiedByName;

/** Did the pouch hold what was counted into it? Null when nobody has opened it yet. */
const cameUpShort = (entry: LedgerEntry): boolean =>
  entry.found !== null &&
  (entry.found.usdMinor !== entry.amount.usdMinor || entry.found.hnlMinor !== entry.amount.hnlMinor);

export interface LedgerSubject {
  id: string;
  name: string;
}

/**
 * WHERE THE MONEY CAME FROM OR WENT TO, from the point of view of the box being read.
 *
 * The endpoint hands back both ends of every movement, and printing both puts "Safe → Safe" on
 * half the rows of the screen you opened to read the safe. Only the OTHER end is news, and a
 * deposit has no other end on file — the bank is not a cash box here — so it says so in words
 * rather than leaving the column blank, which would read as a movement we failed to record.
 */
function otherEnd(entry: LedgerEntry): string {
  if (entry.kind === 'bankDeposit') return 'The bank';
  const other = entry.kind === 'transferIn' ? entry.fromName : entry.toName;
  return other ?? 'Not recorded';
}

/**
 * The other end of a bill paid out of the box: the supplier, named and walkable-to.
 *
 * The ledger row carries only the id of the payment that caused it, and the payment carries the
 * supplier — so the vendor's name is read through the payments list, and the link goes to their
 * page, where the bill and the payment this movement settled are both on file. A link rather than
 * a bare name because the moment anybody reads this row is the moment they want the paperwork.
 */
function SupplierEnd({ supplierId }: { supplierId: string }) {
  const suppliers = useEntityIndex('supplier');
  return (
    <Anchor component={Link} to={`/suppliers/${supplierId}`} size="sm">
      {suppliers.label(supplierId)}
    </Anchor>
  );
}

export function CashBoxLedger({ box, onClose }: { box: LedgerSubject | null; onClose: () => void }) {
  const ledger = useQuery({
    queryKey: listKey('/admin/v1/cash/ledger', { cashLocationId: box?.id }),
    queryFn: () =>
      rawApi<LedgerEntry[]>(
        'GET',
        `/admin/v1/cash/ledger?cashLocationId=${encodeURIComponent(box?.id ?? '')}`,
      ),
    enabled: Boolean(box?.id),
  });

  /*
   * The supplier behind each box payment, read through the payments list. A box event names only
   * the payment that caused it — the payment is what knows the vendor — and both halves of an
   * undone payment appear on that list, so a `paidIn`'s reversal row resolves the same way. Only
   * fetched once a row actually needs it: a safe full of drops and deposits never asks.
   */
  const wantsSuppliers = (ledger.data ?? []).some((e) => e.linkedEntityType === 'supplierPayment');
  const payments = useQuery({
    queryKey: listKey('/admin/v1/suppliers/supplier-payments'),
    queryFn: () =>
      rawApi<{ payment: { id: string }; supplierId: string }[]>(
        'GET',
        '/admin/v1/suppliers/supplier-payments',
      ),
    enabled: wantsSuppliers,
  });
  const supplierOf = new Map((payments.data ?? []).map((row) => [row.payment.id, row.supplierId]));

  const failure = ledger.isError ? errorCopy(ledger.error, 'read this cash box') : null;

  return (
    <Modal opened={Boolean(box)} onClose={onClose} title={box ? `Movements — ${box.name}` : ''} size="xl">
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Everything that has gone into or out of this box, newest first. This is the working behind the
          figure on the register screen: nothing here is typed in, and nothing here can be changed.
        </Text>
        {failure ? (
          <Alert color="red" title={failure.title}>
            {failure.message}
          </Alert>
        ) : (
          <DataTable
            withTableBorder
            minHeight={140}
            fetching={ledger.isFetching}
            records={ledger.data ?? []}
            idAccessor="id"
            noRecordsText="Nothing has moved in or out of this box."
            columns={[
              { accessor: 'at', title: 'When', render: (e) => formatDateTime(e.at) },
              {
                accessor: 'kind',
                title: 'What happened',
                render: (e) => (
                  <Group gap={6} wrap="nowrap">
                    <Badge variant="light" color={enumColor('cashMovement', e.kind)}>
                      {enumLabel('cashMovement', e.kind)}
                    </Badge>
                    {/* A drop is a transfer with a story: the same movement means something
                        different at one in the morning than it does at eight in the evening. */}
                    {e.safeDropKind && (
                      <Text size="xs" c="dimmed">
                        {enumLabel('safeDrop', e.safeDropKind)}
                      </Text>
                    )}
                    {/* The paper half of the owner's rule — receipts plus cash should still total.
                        Said only when a photo is on file: a paid-out without one is not accused of
                        anything, since the bill itself may carry the paper. */}
                    {e.receiptPhotoR2Key && (
                      <Text size="xs" c="dimmed">
                        Receipt on file
                      </Text>
                    )}
                  </Group>
                ),
              },
              {
                accessor: 'fromName',
                title: 'Other side',
                // On a bill paid out of the box the other end is the vendor, known only through the
                // payment that caused the movement — see `SupplierEnd`. Everything else keeps the
                // box-to-box answer.
                render: (e) => {
                  const supplierId =
                    e.linkedEntityType === 'supplierPayment' && e.linkedEntityId
                      ? supplierOf.get(e.linkedEntityId)
                      : undefined;
                  if (supplierId) return <SupplierEnd supplierId={supplierId} />;
                  if (e.kind === 'paidOut' || e.kind === 'paidIn') {
                    return (
                      <Text size="sm" c="dimmed">
                        A supplier
                      </Text>
                    );
                  }
                  return otherEnd(e);
                },
              },
              { accessor: 'amount', title: 'Amount', render: (e) => <MoneyPair pair={e.amount} /> },
              { accessor: 'byName', title: 'Who', render: (e) => e.byName ?? '—' },
              {
                accessor: 'checkedByName',
                title: 'Checked by',
                /*
                 * "Not yet" and not "Nobody": nothing waits for this check and a movement he has
                 * not got to is not a movement that went unwatched. The pouch that did not hold
                 * what was counted into it says so here, against the name of the person who found
                 * that out — the one row on this ledger he needs to see before he closes the modal.
                 */
                render: (e) => {
                  // A bill paid out of the box is never checked row by row — the next count of the
                  // box answers for all of them at once, receipts plus cash against the standing
                  // float. "Not yet" here would promise a per-row check no screen will ever perform.
                  if (e.kind === 'paidOut' || e.kind === 'paidIn') {
                    return (
                      <Text size="sm" c="dimmed">
                        —
                      </Text>
                    );
                  }
                  const who = checkedBy(e);
                  if (!who)
                    return (
                      <Text size="sm" c="dimmed">
                        Not yet
                      </Text>
                    );
                  return (
                    <Stack gap={0}>
                      <Text size="sm">{who}</Text>
                      {/* What was in it, printed rather than differenced. The amount counted into
                          the pouch is the column to the left; a variance worked out here would be a
                          second opinion about a figure the server already computes. */}
                      {cameUpShort(e) && (
                        <Group gap={4} wrap="nowrap">
                          <Text size="xs" c="red">
                            Found
                          </Text>
                          <MoneyPair pair={e.found} />
                        </Group>
                      )}
                    </Stack>
                  );
                },
              },
              { accessor: 'reason', title: 'Note', render: (e) => e.reason ?? '—' },
            ]}
          />
        )}
      </Stack>
    </Modal>
  );
}
